import { isPresent } from './derivePricingResolution';
import type {
    BorrowStatus as BorrowStatusKey,
    PoolPricingConfig,
    PoolPricingSimulationResponse,
    PoolPricingWinner,
} from '@thinkalpha/locates-ws-client';

/** Mirrors the backend's `roundPrice`: prices round to 4 decimal places. */
function roundPrice(price: number): number {
    return Math.round(price * 10000 + 1e-15) / 10000;
}

function floorFor(config: PoolPricingConfig, borrowStatus: BorrowStatusKey | null): number | null {
    if (!config.minimumPrice?.enabled || borrowStatus === null) return null;
    const floor = config.minimumPrice.prices[borrowStatus];
    return isPresent(floor) ? floor : null;
}

/**
 * Locally re-resolves a simulation against a DRAFT config, reusing the
 * server-provided market inputs (candidates, borrow status) from a
 * saved-config run — candidates are config-independent, so a reader-scoped
 * pool simulation supplies everything an unsaved-edit preview needs
 * without the superuser-only arbitrary-config endpoint.
 *
 * Mirrors the backend's `resolvePoolPrice` exactly — first usable
 * (finite, non-negative, including zero) candidate wins, multiplier
 * before floor, `winner` names the floor when it lifted the price,
 * result rounded to 4 decimals.
 */
export function previewDraftSimulation(
    config: PoolPricingConfig,
    base: PoolPricingSimulationResponse,
): PoolPricingSimulationResponse {
    const winningMethod = (config.hierarchy ?? []).find((method) => isPresent(base.candidates[method])) ?? null;
    let price: number | null = winningMethod === null ? null : (base.candidates[winningMethod] ?? null);
    let winner: PoolPricingWinner | null = winningMethod;
    if (
        price !== null &&
        config.multiplier?.enabled &&
        typeof config.multiplier.multiplier === 'number' &&
        config.multiplier.multiplier > 0
    ) {
        price *= config.multiplier.multiplier;
    }
    // The floor only participates when a method priced — the backend's
    // resolvePoolPrice returns early with no price AND no floor when
    // nothing in the hierarchy resolves.
    const floor = price === null ? null : floorFor(config, base.borrowStatus);
    if (price !== null && floor !== null && price < floor) {
        price = floor;
        winner = 'minimumPrice';
    }
    return {
        symbol: base.symbol,
        config,
        candidates: base.candidates,
        borrowStatus: base.borrowStatus,
        winner,
        price: price === null ? null : roundPrice(price),
        minimumPrice: floor,
        // The UI cannot edit quoteVolume, so the draft's factor is always
        // the saved one the server already computed.
        quoteVolumeFactor: base.quoteVolumeFactor,
    };
}
